import { useEffect, useState } from 'react';
import { Bell, BellOff, Menu, RotateCcw } from 'lucide-react';
import { BACKOFFICE_NAVIGATION_ITEMS } from '@/components/layout/backoffice-navigation-items';
import { PageHeader } from '@/components/shared';
import { getTagColorClassName } from '@/components/shared/tag-colors';
import { useBackofficePageTitle } from '@/lib/page-title';
import {
  DEFAULT_BACKOFFICE_NAVIGATION_PREFERENCE,
  resetAllBackofficePreferences,
  type BackofficeNavigationPreferenceValue,
  useBackofficeNavigationPreference,
} from '@/preferences';
import {
  getBackofficePushNotificationSettings,
  disableBackofficePushNotifications,
  enableBackofficePushNotifications,
  getCurrentPushSubscription,
  getPushNotificationFailureMessage,
  getPushNotificationStatus,
  type PushNotificationStatus,
  updateBackofficePushNotificationSettings,
} from './push-notifications';
import { NavigationSettingsModal, ResetPreferencesConfirmationModal } from './components';

const statusCopy: Record<PushNotificationStatus, { title: string; body: string }> = {
  unsupported: {
    title: 'Browser notifications unavailable',
    body: 'This browser or device does not support push notifications.',
  },
  'missing-key': {
    title: 'Push notifications not configured',
    body: 'A VAPID public key must be configured before push notifications can be enabled.',
  },
  default: {
    title: 'Browser notifications off',
    body: 'Enable browser notifications for backoffice updates on this device.',
  },
  denied: {
    title: 'Browser notifications blocked',
    body: 'Notifications are blocked in your browser settings for this site.',
  },
  enabled: {
    title: 'Browser notifications on',
    body: 'This browser will receive backoffice notifications.',
  },
  disabled: {
    title: 'Browser notifications off',
    body: 'Notification permission is granted, but this browser is not subscribed.',
  },
};

export function BackofficeSettingsPage() {
  useBackofficePageTitle('Settings');

  const {
    hiddenItemIds,
    loaded: navigationLoaded,
    orderedItemIds,
    orderedItems,
    resetNavigationPreference,
    saveNavigationPreference,
  } = useBackofficeNavigationPreference();
  const [status, setStatus] = useState<PushNotificationStatus>('unsupported');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [navigationModalOpen, setNavigationModalOpen] = useState(false);
  const [navigationSubmitting, setNavigationSubmitting] = useState(false);
  const [navigationError, setNavigationError] = useState<string | null>(null);
  const [internalTesterNotificationsEnabled, setInternalTesterNotificationsEnabled] =
    useState(false);
  const [suppressionLoaded, setSuppressionLoaded] = useState(false);
  const [suppressionBusy, setSuppressionBusy] = useState(false);
  const [suppressionError, setSuppressionError] = useState<string | null>(null);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetSuccess, setResetSuccess] = useState<string | null>(null);

  async function refreshPushNotificationState() {
    const subscription = await getCurrentPushSubscription();
    setStatus(getPushNotificationStatus(subscription));
    const settings = await getBackofficePushNotificationSettings(subscription);
    setInternalTesterNotificationsEnabled(settings.internalTesterNotifications);
    setSuppressionLoaded(true);
  }

  useEffect(() => {
    void refreshPushNotificationState().catch((nextError) => {
      setSuppressionError(
        nextError instanceof Error
          ? nextError.message
          : 'Failed to load push notification settings.'
      );
      setSuppressionLoaded(true);
    });
  }, []);

  const canEnable = status === 'default' || status === 'disabled';
  const canDisable = status === 'enabled';
  const canToggle = canEnable || canDisable;
  const isEnabled = status === 'enabled';
  const isSuppressionControlDisabled = !isEnabled || !suppressionLoaded || suppressionBusy;
  const copy = statusCopy[status];

  async function handleToggle() {
    if (!canToggle || busy) {
      return;
    }

    setBusy(true);
    setError(null);
    try {
      if (isEnabled) {
        await disableBackofficePushNotifications();
      } else {
        await enableBackofficePushNotifications();
      }
      await refreshPushNotificationState();
    } catch (nextError) {
      setError(
        isEnabled
          ? 'Failed to disable notifications.'
          : await getPushNotificationFailureMessage(nextError)
      );
      await refreshPushNotificationState();
    } finally {
      setBusy(false);
    }
  }

  async function handleSaveNavigationPreference(value: BackofficeNavigationPreferenceValue) {
    if (navigationSubmitting) {
      return;
    }

    setNavigationSubmitting(true);
    setNavigationError(null);
    try {
      await saveNavigationPreference(value);
      setNavigationModalOpen(false);
    } catch (nextError) {
      setNavigationError(
        nextError instanceof Error ? nextError.message : 'Failed to save navigation settings.'
      );
    } finally {
      setNavigationSubmitting(false);
    }
  }

  async function handleResetPreferences() {
    if (resetSubmitting) {
      return;
    }

    setResetSubmitting(true);
    setResetError(null);
    setResetSuccess(null);
    try {
      const deletedCount = await resetAllBackofficePreferences();
      resetNavigationPreference();
      setResetModalOpen(false);
      setResetSuccess(
        deletedCount === 0
          ? 'No saved preferences were found.'
          : `Reset ${deletedCount} saved ${deletedCount === 1 ? 'preference' : 'preferences'}.`
      );
    } catch (nextError) {
      setResetError(
        nextError instanceof Error ? nextError.message : 'Failed to reset saved preferences.'
      );
    } finally {
      setResetSubmitting(false);
    }
  }

  async function handleSuppressionToggle() {
    if (!suppressionLoaded || suppressionBusy) {
      return;
    }

    const nextValue = !internalTesterNotificationsEnabled;
    setSuppressionBusy(true);
    setSuppressionError(null);
    try {
      const subscription = await getCurrentPushSubscription();
      if (!subscription) {
        throw new Error('Browser push notifications must be enabled first.');
      }
      const settings = await updateBackofficePushNotificationSettings(
        {
          internalTesterNotifications: nextValue,
        },
        subscription
      );
      setInternalTesterNotificationsEnabled(settings.internalTesterNotifications);
    } catch (nextError) {
      setSuppressionError(
        nextError instanceof Error
          ? nextError.message
          : 'Failed to save push notification settings.'
      );
    } finally {
      setSuppressionBusy(false);
    }
  }

  return (
    <main className="min-h-[320px] bg-white p-6 md:p-8" aria-label="Backoffice main content">
      <div className="flex max-w-3xl flex-col gap-5">
        <PageHeader showShareButton={false}>Settings</PageHeader>

        <section
          aria-labelledby="push-notifications-title"
          className="rounded-lg border border-neutral-200 bg-white p-5"
        >
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex min-w-0 gap-3">
                <div className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-backoffice-50 text-backoffice-600">
                  {status === 'enabled' ? (
                    <Bell className="h-5 w-5" aria-hidden="true" />
                  ) : (
                    <BellOff className="h-5 w-5" aria-hidden="true" />
                  )}
                </div>
                <div className="min-w-0">
                  <h2
                    id="push-notifications-title"
                    className="text-lg font-semibold text-neutral-900"
                  >
                    Push notifications
                  </h2>
                  <p className="mt-2 text-body text-neutral-600">{copy.body}</p>
                  <p className="mt-2 text-small font-medium text-neutral-700">{copy.title}</p>
                  {error ? <p className="mt-2 text-small text-red-700">{error}</p> : null}
                </div>
              </div>

              <div className="flex shrink-0 gap-2">
                <button
                  type="button"
                  role="switch"
                  aria-checked={isEnabled}
                  aria-label="Push notifications"
                  disabled={!canToggle || busy}
                  onClick={() => {
                    void handleToggle();
                  }}
                  className={`focus-ring relative inline-flex h-7 w-12 shrink-0 items-center rounded-full border transition disabled:cursor-not-allowed disabled:opacity-60 ${
                    isEnabled
                      ? 'border-backoffice-500 bg-backoffice-500'
                      : 'border-neutral-300 bg-neutral-200'
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`block h-5 w-5 rounded-full bg-white shadow-sm transition ${
                      isEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0 sm:pl-12">
                <h3
                  className={`text-base font-semibold ${
                    isEnabled ? 'text-neutral-900' : 'text-neutral-400'
                  }`}
                >
                  <span className="inline-flex flex-wrap items-center gap-2">
                    <span>Send notifications for</span>
                    <span
                      className={[
                        'inline-flex items-center rounded-full border px-2 py-1 text-small font-medium',
                        getTagColorClassName('lavender'),
                      ].join(' ')}
                    >
                      Internal tester
                    </span>
                  </span>
                </h3>
                <p
                  className={`mt-2 text-body ${
                    isEnabled ? 'text-neutral-600' : 'text-neutral-400'
                  }`}
                >
                  Send push notifications for events related to users tagged with Internal tester.
                </p>
                <p
                  className={`mt-2 text-small font-medium ${
                    isEnabled ? 'text-neutral-700' : 'text-neutral-400'
                  }`}
                >
                  {internalTesterNotificationsEnabled
                    ? 'Notifications for Internal tester are sent in this browser.'
                    : 'Notifications for Internal tester are not sent in this browser.'}
                </p>
                {suppressionError ? (
                  <p className="mt-2 text-small text-red-700">{suppressionError}</p>
                ) : null}
              </div>

              <div className="flex shrink-0 gap-2">
                <button
                  type="button"
                  role="switch"
                  aria-checked={internalTesterNotificationsEnabled}
                  aria-label="Send notifications for Internal tester"
                  disabled={isSuppressionControlDisabled}
                  onClick={() => {
                    void handleSuppressionToggle();
                  }}
                  className={`focus-ring relative inline-flex h-7 w-12 shrink-0 items-center rounded-full border transition disabled:cursor-not-allowed disabled:opacity-60 ${
                    internalTesterNotificationsEnabled
                      ? 'border-backoffice-500 bg-backoffice-500'
                      : 'border-neutral-300 bg-neutral-200'
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`block h-5 w-5 rounded-full bg-white shadow-sm transition ${
                      internalTesterNotificationsEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </section>

        <section
          aria-labelledby="navigation-settings-title"
          className="rounded-lg border border-neutral-200 bg-white p-5"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex min-w-0 gap-3">
              <div className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-backoffice-50 text-backoffice-600">
                <Menu className="h-5 w-5" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <h2
                  id="navigation-settings-title"
                  className="text-lg font-semibold text-neutral-900"
                >
                  Navigation menu
                </h2>
                <p className="mt-2 text-body text-neutral-600">
                  Choose which links appear in the backoffice menu and arrange them in the order you
                  want.
                </p>
                <p className="mt-2 text-small font-medium text-neutral-700">
                  Current menu:{' '}
                  {orderedItems
                    .filter((item) => !hiddenItemIds.has(item.id))
                    .map((item) => item.label)
                    .join(', ')}
                </p>
              </div>
            </div>

            <div className="flex shrink-0 gap-2">
              <button
                type="button"
                className="focus-ring inline-flex h-10 items-center gap-1.5 whitespace-nowrap rounded-lg border border-backoffice-500 bg-backoffice-500 px-3 text-small font-medium text-white hover:bg-backoffice-600 disabled:cursor-not-allowed disabled:bg-backoffice-300"
                disabled={!navigationLoaded}
                onClick={() => {
                  setNavigationError(null);
                  setNavigationModalOpen(true);
                }}
              >
                <Menu className="h-4 w-4" aria-hidden="true" />
                Configure navigation
              </button>
            </div>
          </div>
        </section>

        <section
          aria-labelledby="reset-preferences-title"
          className="rounded-lg border border-neutral-200 bg-white p-5"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex min-w-0 gap-3">
              <div className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-backoffice-50 text-backoffice-600">
                <RotateCcw className="h-5 w-5" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <h2 id="reset-preferences-title" className="text-lg font-semibold text-neutral-900">
                  Reset saved preferences
                </h2>
                <p className="mt-2 text-body text-neutral-600">
                  Clear saved layouts, filters, sorting, tile settings, and media settings for your
                  backoffice user.
                </p>
                {resetSuccess ? (
                  <p className="mt-2 text-small font-medium text-green-700">{resetSuccess}</p>
                ) : null}
              </div>
            </div>

            <div className="flex shrink-0 gap-2">
              <button
                type="button"
                className="focus-ring inline-flex h-10 items-center gap-1.5 whitespace-nowrap rounded-lg border border-backoffice-500 bg-backoffice-500 px-3 text-small font-medium text-white hover:bg-backoffice-600"
                onClick={() => {
                  setResetError(null);
                  setResetSuccess(null);
                  setResetModalOpen(true);
                }}
              >
                <RotateCcw className="h-4 w-4" aria-hidden="true" />
                Reset preferences
              </button>
            </div>
          </div>
        </section>
      </div>

      {resetModalOpen ? (
        <ResetPreferencesConfirmationModal
          submitting={resetSubmitting}
          error={resetError}
          onClose={() => {
            if (!resetSubmitting) {
              setResetModalOpen(false);
            }
          }}
          onConfirm={handleResetPreferences}
        />
      ) : null}

      {navigationModalOpen ? (
        <NavigationSettingsModal
          items={BACKOFFICE_NAVIGATION_ITEMS}
          defaultValue={DEFAULT_BACKOFFICE_NAVIGATION_PREFERENCE}
          initialValue={{
            orderedItemIds,
            hiddenItemIds: Array.from(hiddenItemIds),
          }}
          submitting={navigationSubmitting}
          error={navigationError}
          onClose={() => {
            if (!navigationSubmitting) {
              setNavigationModalOpen(false);
            }
          }}
          onSave={handleSaveNavigationPreference}
        />
      ) : null}
    </main>
  );
}
